import React, { useEffect, useRef } from 'react';
import { FaExclamationCircle, FaTimes, FaVolumeMute, FaVolumeUp } from 'react-icons/fa';
import { ChannelSidebar } from './ChannelSidebar';
import { MessageArea } from './MessageArea';
import { CloseButton } from '../common';
import { useChat } from '../../hooks/useChat';
import { useChatSoundPreference } from '../../hooks/useChatSoundPreference';
import { useGameInstance } from '../../contexts/GameInstanceContext';
import { restorePhaserKeyboard } from '../../utils/phaser-keyboard.js';

interface ChatDrawerProps {
  onWidthChange?: (width: number) => void;
}

/** The chat drawer. Whether it is open is the chat's own state (`useChat`). */
export const ChatDrawer: React.FC<ChatDrawerProps> = ({ onWidthChange }) => {
  const drawerRef = useRef<HTMLDivElement>(null);
  const { gameInstance } = useGameInstance();
  // Read by the close effect below, which must run on open/close only: a game
  // created or destroyed while the drawer is shut is not a reason to blur the
  // focused field or grab focus for the canvas
  const gameRef = useRef(gameInstance);
  useEffect(() => {
    gameRef.current = gameInstance;
  }, [gameInstance]);
  const {
    isOpen,
    close: onClose,
    channels,
    activeChannel,
    selectChannel,
    history,
    unreadCounts,
    notice,
    dismissNotice,
    sendMessage,
    retryMessage,
    retryJoin,
    startDm,
    loadOlderMessages
  } = useChat();
  const chatSound = useChatSoundPreference();

  // Handle escape key to close drawer. The key is marked as handled, so a
  // layer underneath (full-screen video) does not act on the same press.
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Handle click outside drawer
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) {
        // Check if click is on the overlay
        const target = e.target as HTMLElement;
        if (target.classList.contains('chat-overlay')) {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Report drawer width changes
  useEffect(() => {
    if (onWidthChange) {
      if (drawerRef.current && isOpen) {
        // Use a ResizeObserver to detect actual width changes
        const resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            onWidthChange(entry.contentRect.width);
          }
        });

        resizeObserver.observe(drawerRef.current);
        
        // Report initial width for immediate bounds adjustment
        onWidthChange(drawerRef.current.offsetWidth);

        return () => {
          resizeObserver.disconnect();
        };
      } else {
        // Report 0 width when closed
        onWidthChange(0);
      }
    }
  }, [isOpen, onWidthChange]);

  // Hand the keyboard back to the game when the drawer closes
  useEffect(() => {
    if (!isOpen) {
      // Blur any active input to trigger blur handlers
      const activeElement = document.activeElement as HTMLElement;
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
        activeElement.blur();
      }

      // Re-enable the Phaser keyboard unless something still holds it: under
      // full-screen video or an open modal, closing the chat must not let the
      // arrow keys walk the avatar (`utils/phaser-keyboard.ts`)
      const keyboardEnabled = restorePhaserKeyboard(gameRef.current);

      // Focus the game canvas to restore keyboard control. Not while the
      // keyboard stays suspended: the canvas is covered then, and focus
      // belongs to whatever covers it.
      const canvas = document.querySelector('.phaser-game-canvas') as HTMLCanvasElement;
      if (canvas && keyboardEnabled) {
        // Small delay to ensure focus works after drawer animation
        setTimeout(() => {
          canvas.focus();
        }, 50);
      }
    }
  }, [isOpen]);

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="chat-overlay fixed inset-0 bg-black/30 z-[999] md:hidden"
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`chat-drawer fixed left-0 top-0 bottom-16 w-full md:w-[600px] bg-game-dark border-r border-gray-700 
                    transform transition-transform duration-200 ease-in-out z-[2000]
                    ${isOpen ? 'translate-x-0' : '-translate-x-full'}
                    shadow-2xl`}
      >
        <div className="flex h-full">
          {/* Channel Sidebar */}
          <ChannelSidebar
            channels={channels}
            activeChannel={activeChannel}
            onChannelSelect={(channel) => selectChannel(channel.id)}
            unreadCounts={unreadCounts}
            onStartDM={startDm}
          />

          {/* Message Area */}
          <div className="flex-1 flex flex-col h-full">
            {/* Header */}
            <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-700 bg-game-dark/50">
              <div className="flex items-center gap-2">
                {activeChannel && (
                  <>
                    <span className="text-gray-300 text-sm">
                      {activeChannel.type === 'public' ? '#' : ''}
                    </span>
                    <h2 className="text-white font-semibold">
                      {activeChannel.displayName || activeChannel.name}
                    </h2>
                  </>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={chatSound.toggle}
                  className="p-1 text-gray-400 hover:text-white transition-colors rounded hover:bg-game-medium/30"
                  aria-label={chatSound.enabled ? 'Mute message sound' : 'Unmute message sound'}
                  aria-pressed={!chatSound.enabled}
                  title={chatSound.enabled ? 'Mute message sound' : 'Unmute message sound'}
                >
                  {chatSound.enabled ? <FaVolumeUp /> : <FaVolumeMute />}
                </button>
                <button
                  onClick={onClose}
                  className="p-1 text-gray-400 hover:text-white transition-colors rounded hover:bg-game-medium/30"
                  aria-label="Close chat"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            {/* A chat failure with nowhere else to show it (a DM that did not open) */}
            {notice && (
              <div
                role="alert"
                className="flex-shrink-0 flex items-center gap-3 px-4 py-2 text-sm text-white bg-game-orange/20 border-b border-game-orange"
              >
                <FaExclamationCircle className="flex-shrink-0 text-game-orange" aria-hidden="true" />
                <span className="flex-1">{notice}</span>
                <CloseButton size="sm" onClick={dismissNotice} aria-label="Dismiss" title="Dismiss" />
              </div>
            )}

            {/* Messages - flex-1 with min-h-0 ensures proper container sizing */}
            <div className="flex-1 min-h-0">
              {activeChannel && history ? (
                <MessageArea
                  channel={activeChannel}
                  history={history}
                  onSendMessage={sendMessage}
                  onRetryMessage={retryMessage}
                  onRetryJoin={retryJoin}
                  onLoadMore={loadOlderMessages}
                />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400">
                  Select a channel to start chatting
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};